import React from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  BookOpenText,
  Box,
  BriefcaseBusiness,
  Download,
  FolderKanban,
  GraduationCap,
  Lightbulb,
  Mail,
  MailCheck,
  MapPinned,
  PenSquare,
  PhoneCall,
  Users,
} from "lucide-react";
import ContentManager from "../components/ContentManager";
import UsersManager from "../components/UsersManager";
import BlogManager from "../components/BlogManager";
import BoardsManager from "../components/BoardsManager";
import AccessoriesManager from "../components/MetalsManager";
import MetalFramesManager from "../components/MetalFramesManager";
import ProjectsManager from "../components/ProjectsManager";
import SolutionsManager from "../components/SolutionsManager";
import SectorsManager from "../components/SectorsManager";
import DownloadsManager from "../components/DownloadsManager";
import CoursesManager from "../components/CoursesManager";
import TrainingsManager from "../components/TrainingsManager";
import CallsRequestedManager from "../components/CallsRequestedManager";
import ContactUsManager from "../components/ContactUsManager";
import SubmittedQuestionsManager from "../components/SubmittedQuestionsManager";
import CareersManager from "../components/CareersManager";
import SubscribersManager from "../components/SubscribersManager";
import ProductsCategoryTabs from "../components/ProductsCategoryTabs";
import DashboardOverview from "../components/DashboardOverview";
import DashboardSectionShell from "./DashboardSectionShell";
import { DashboardPanel } from "../components/dashboardUi";
import {
  CONTENT_PAGES,
  DEFAULT_CONTENT_PAGE,
  DEFAULT_PRODUCT_TYPE,
  PRODUCT_TYPES,
} from "../dashboardNavConfig";

export const DashboardOverviewPage = () => <DashboardOverview />;

export const DashboardContentPage = () => {
  const { t } = useTranslation();
  const { pageKey } = useParams();
  const navigate = useNavigate();
  const validKeys = CONTENT_PAGES.map((p) => p.key);
  const selectedPage = validKeys.includes(pageKey) ? pageKey : DEFAULT_CONTENT_PAGE;

  if (pageKey && !validKeys.includes(pageKey)) {
    return <Navigate to={`/dashboard/content/${DEFAULT_CONTENT_PAGE}`} replace />;
  }

  return (
    <ContentManager
      t={t}
      selectedPage={selectedPage}
      contentPages={CONTENT_PAGES}
      onPageChange={(key) => navigate(`/dashboard/content/${key}`)}
    />
  );
};

export const DashboardProductsPage = () => {
  const { productType } = useParams();
  const navigate = useNavigate();
  const selectedProductType = PRODUCT_TYPES.includes(productType) ? productType : DEFAULT_PRODUCT_TYPE;

  if (productType && !PRODUCT_TYPES.includes(productType)) {
    return <Navigate to={`/dashboard/products/${DEFAULT_PRODUCT_TYPE}`} replace />;
  }

  return (
    <DashboardSectionShell
      eyebrow="Products"
      title="Products Manager"
      icon={Box}
      description={
        <>
          Choose a product type, then add or edit items. Click <strong>Edit</strong> on any row to open the popup
          editor.
        </>
      }
      tabs={
        <ProductsCategoryTabs
          value={selectedProductType}
          onChange={(type) => navigate(`/dashboard/products/${type}`)}
        />
      }
    >
      <DashboardPanel>
        {selectedProductType === "boards" ? (
          <BoardsManager />
        ) : selectedProductType === "accessories" ? (
          <AccessoriesManager />
        ) : selectedProductType === "drywall-metal-frame" ? (
          <MetalFramesManager frameType="drywall-metal-frame" title="Drywall Metal Frames" />
        ) : (
          <MetalFramesManager frameType="ceiling-metal-frame" title="Ceiling Metal Frames" />
        )}
      </DashboardPanel>
    </DashboardSectionShell>
  );
};

export const DashboardSolutionsPage = () => (
  <DashboardSectionShell
    eyebrow="Solutions"
    title="Solutions Editor"
    icon={Lightbulb}
    description="Create and update solution entries used by solution detail pages."
  >
    <DashboardPanel>
      <SolutionsManager />
    </DashboardPanel>
  </DashboardSectionShell>
);

export const DashboardSectorsPage = () => (
  <DashboardSectionShell
    eyebrow="Sectors"
    title="Sectors Editor"
    icon={MapPinned}
    description="Manage sectors content, images, benefits, project block and gallery."
  >
    <DashboardPanel>
      <SectorsManager />
    </DashboardPanel>
  </DashboardSectionShell>
);

export const DashboardCareersPage = () => (
  <DashboardSectionShell
    eyebrow="Careers"
    title="Careers Manager"
    icon={BriefcaseBusiness}
    description="Manage jobs and review submitted applications."
  >
    <CareersManager />
  </DashboardSectionShell>
);

export const DashboardCoursesPage = () => (
  <DashboardSectionShell
    eyebrow="Courses"
    title="Courses Editor"
    icon={BookOpenText}
    description="Manage courses, descriptions and gain items for academy pages."
  >
    <DashboardPanel>
      <CoursesManager />
    </DashboardPanel>
  </DashboardSectionShell>
);

export const DashboardTrainingsPage = () => (
  <DashboardSectionShell
    eyebrow="Trainings"
    title="Trainings Editor"
    icon={GraduationCap}
    description="Manage course trainings, benefits and trainer avatars per course."
  >
    <DashboardPanel>
      <TrainingsManager />
    </DashboardPanel>
  </DashboardSectionShell>
);

export const DashboardDownloadsPage = () => (
  <DashboardSectionShell
    eyebrow="Downloads"
    title="Downloads Editor"
    icon={Download}
    description="Manage downloadable PDF files, titles, language and active status."
  >
    <DashboardPanel>
      <DownloadsManager />
    </DashboardPanel>
  </DashboardSectionShell>
);

export const DashboardCallsRequestedPage = () => (
  <DashboardSectionShell
    eyebrow="Calls Requested"
    title="Calls Requested"
    icon={PhoneCall}
    description="Manage call request services and review incoming call requests."
  >
    <CallsRequestedManager />
  </DashboardSectionShell>
);

export const DashboardContactUsPage = () => (
  <DashboardSectionShell
    eyebrow="Contact Us"
    title="Contact Us Requests"
    icon={Mail}
    description="Review contact form submissions and submitted questions from visitors."
  >
    <DashboardPanel>
      <ContactUsManager />
    </DashboardPanel>
    <DashboardPanel title="Submitted questions" description="Questions sent through contact flows.">
      <SubmittedQuestionsManager />
    </DashboardPanel>
  </DashboardSectionShell>
);

export const DashboardSubscribersPage = () => (
  <DashboardSectionShell
    eyebrow="Subscribed"
    title="Newsletter Subscribers"
    icon={MailCheck}
    description="See who has subscribed from the footer newsletter form and remove entries when needed."
  >
    <DashboardPanel>
      <SubscribersManager />
    </DashboardPanel>
  </DashboardSectionShell>
);

export const DashboardProjectsPage = () => (
  <DashboardSectionShell
    eyebrow="Projects"
    title="Projects Manager"
    icon={FolderKanban}
    description="CRUD projects and manage project gallery images for the portfolio."
  >
    <ProjectsManager />
  </DashboardSectionShell>
);

export const DashboardUsersPage = () => (
  <DashboardSectionShell
    eyebrow="Users"
    title="Users Manager"
    icon={Users}
    description="Manage dashboard accounts, roles and access."
  >
    <DashboardPanel>
      <UsersManager />
    </DashboardPanel>
  </DashboardSectionShell>
);

export const DashboardBlogPage = () => (
  <DashboardSectionShell
    eyebrow="Blog"
    title="Blog Manager"
    icon={PenSquare}
    description="Create posts, translations, sections and cover images for the blog."
  >
    <DashboardPanel>
      <BlogManager />
    </DashboardPanel>
  </DashboardSectionShell>
);
